import { IAppContext } from 'App/AppContext';
import { FilterTaskGroupKeys } from 'components/Tasks/Filter/FilterTaskGroupKeys';
import { TagSystemTypes } from 'models/tag';
import { TFunction } from 'i18next';
import { ITaskCustomView } from 'components/Tasks/TaskViews/TaskViewTypes';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { getCustomViewRouteId, getCustomViewUrl } from './TaskViewUtils';

// The API rejects a name that collides with an own or org-shared view with a
// 400 carrying TASKVIEW0001 -- never a 409.
export const isDuplicateViewNameError = (err: unknown): boolean =>
  AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_TaskView_DuplicateName;

export const myTaskKey = 'mytasks';
export const allTaskKey = 'alltasks';
export const templateTaskKey = 'templates';
export const myRequestKey = 'myrequests';
export const monitoringFindingTaskKey = 'monitoringfinding';
export const nonConformityTaskKey = 'nonconformity';
export const incidentTaskKey = 'incident';

export const standardCategoryViewKeys = [
  monitoringFindingTaskKey,
  nonConformityTaskKey,
  incidentTaskKey,
];

export const isStandardCategoryViewKey = (viewKey: string | undefined): boolean => {
  return viewKey !== undefined && standardCategoryViewKeys.includes(viewKey);
};

export const getDefaultStandardViewFilters = (): Record<string, string[]> => {
  return {
    [monitoringFindingTaskKey]: [`${FilterTaskGroupKeys.tagCategory}${TagSystemTypes.MonitoringFinding}`],
    [nonConformityTaskKey]: [`${FilterTaskGroupKeys.tagCategory}${TagSystemTypes.NonConformity}`],
    [incidentTaskKey]: [`${FilterTaskGroupKeys.tagCategory}${TagSystemTypes.Incident}`],
  };
};

export const getDefaultTemplateViewKeys = (isManager: boolean): string[] => {
  return [
    myTaskKey,
    myRequestKey,
    ...(isManager ? [allTaskKey, templateTaskKey] : []),
  ];
};

export const getDefaultHiddenTemplateViewKeys = (): string[] => {
  return [
    monitoringFindingTaskKey,
    nonConformityTaskKey,
    incidentTaskKey,
  ];
};

export const getInitialHiddenTemplateViewKeys = (
  persistedHiddenTemplateViewKeys: string[] | undefined,
  persistedViewOrder: string[] | undefined,
): string[] => {
  const defaultHiddenTemplateKeys = getDefaultHiddenTemplateViewKeys();
  const hasExplicitSpecialTemplateOrder = Boolean(
    persistedViewOrder?.some((key) => defaultHiddenTemplateKeys.includes(key)),
  );

  if (persistedHiddenTemplateViewKeys && (persistedHiddenTemplateViewKeys.length > 0 || hasExplicitSpecialTemplateOrder)) {
    return persistedHiddenTemplateViewKeys;
  }

  return defaultHiddenTemplateKeys;
};

export const isSourceTemplateAllowed = (sourceKey: string, isManager: boolean): boolean => {
  if (sourceKey === allTaskKey || sourceKey === templateTaskKey) {
    return isManager;
  }

  return true;
};

export const isViewUrlAllowed = (url: string, isManager: boolean): boolean => {
  if (url === '/alltasks' || url === '/templates') {
    return isManager;
  }

  return true;
};

export const isReservedTemplateLabel = (candidate: string, reservedLabels: string[]): boolean => {
  const candidateLabel = (candidate ?? '').trim().toLocaleLowerCase();
  if (!candidateLabel) return false;

  return reservedLabels.some((label) => label.trim().toLocaleLowerCase() === candidateLabel);
};

export type TaskViewLabelError = 'reserved' | 'duplicate' | null;

export interface ITaskViewLabelValidationOpts {
  reservedLabels: string[];
  isDuplicate: (label: string, excludedKey?: string) => boolean;
  excludedKey?: string;
}

export const validateTaskViewLabel = (
  candidate: string,
  opts: ITaskViewLabelValidationOpts,
): TaskViewLabelError => {
  if (isReservedTemplateLabel(candidate, opts.reservedLabels)) return 'reserved';
  if (opts.isDuplicate(candidate, opts.excludedKey)) return 'duplicate';

  return null;
};

export const getTaskViewLabelErrorMessage = (
  error: TaskViewLabelError,
  t: TFunction,
): string | undefined => {
  if (!error) return undefined;

  // Pass each key as a string literal directly into the translation call --
  // the maintainTranslations test scanner only recognises usage when the
  // argument is a quoted literal, not a variable.
  return error === 'reserved'
    ? t('tasks:TaskViews.Errors.TemplateNameCollision')
    : t('tasks:TaskViews.Errors.DuplicateViewName');
};

export const showTaskViewLabelError = (
  error: TaskViewLabelError,
  appContext: IAppContext,
  t: TFunction,
): boolean => {
  const message = getTaskViewLabelErrorMessage(error, t);
  if (!message) return false;
  appContext.showNotification(message, true);

  return true;
};

export const routeToSourceKey = (route: string | null): string => {
  switch (route) {
    case myRequestKey: return myRequestKey;
    case allTaskKey: return allTaskKey;
    case monitoringFindingTaskKey: return monitoringFindingTaskKey;
    case nonConformityTaskKey: return nonConformityTaskKey;
    case incidentTaskKey: return incidentTaskKey;
    default: return myTaskKey;
  }
};

// Normalises a custom-view list: rebuilds each url from the current label,
// downgrades a source template the user is not allowed to use to myTasks, and
// drops duplicate keys. Pure -- isManager is passed in rather than read from a
// hook so both TasksOverview and TaskViewStateProvider can share it.
export const sanitizeCustomViews = (
  views: ITaskCustomView[] | undefined,
  isManager: boolean,
): ITaskCustomView[] => {
  const source = views ?? [];

  return source
    .map((v) => {
      const correctedUrl = getCustomViewUrl(getCustomViewRouteId(v), v.label);
      const allowedSourceKey = isSourceTemplateAllowed(v.sourceTemplateKey, isManager) ? v.sourceTemplateKey : myTaskKey;

      return { ...v, url: correctedUrl, sourceTemplateKey: allowedSourceKey };
    })
    .filter((v, index, arr) => arr.findIndex((x) => x.key === v.key) === index);
};
